// schema

// libraries
import {
  AccountCircle,
  ArrowDropDown,
  ExitToApp,
  Feedback,
  Help,
  School,
  Settings,
} from "@clockwise/design-system/icons";
import { greys } from "@clockwise/web-commons/src/styles/color.styles";
import spacing from "@clockwise/web-commons/src/styles/spacing.styles";
import { Avatar, Divider, ListItemText, Menu, MenuItem } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useRouter } from "found";
import * as React from "react";
import { useSelector } from "react-redux";

// components
import { LogoutBtn } from "#webapp/src/components/logout-btn";

// constants
import { CLOCKWISE_ACADEMY, paths } from "#webapp/src/constants/site.constant";
import { baseUrl } from "@clockwise/client-commons/src/constants/help-center";

// state
import { getOnboardingFlowState, UserWithFlowStates } from "#webapp/src/state/onboarding";
import { IReduxState } from "#webapp/src/state/reducers/root.reducer";

// util
import { track, TrackingEvents } from "#webapp/src/util/analytics.util";
import { Edges, getCurrentOrg, UserWithOrgs } from "#webapp/src/util/org.util";

// internal
import { appPaths } from "@clockwise/web-commons/src/constants/route.constants";
// import { getValue } from "../../../util/errorable.util";
import { useIsOutlook } from "@clockwise/web-commons/src/util/OutlookContext";

const useStyles = makeStyles({
  menus: {
    display: "flex",
    alignItems: "center",
  },
  menu: {
    marginTop: spacing.sm,
  },
  advancedMenu: {
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    fontSize: "15px",
    color: greys.slate,

    "@media (max-width: 768px)": {
      display: "none",
    },
  },
  menuItemTypography: {},
  menuItemIcon: {
    color: greys.slate,
  },
  menuExpandIcon: {
    color: greys.slate,
    marginLeft: 4,
    marginRight: 24,
  },
  menuDivider: {
    marginTop: 6,
    marginBottom: 6,
  },
  menuHeader: {
    marginLeft: 18,
  },
  avatar: {
    cursor: "pointer",
  },
});

interface UserProfile {
  givenName: string | null;
  familyName: string | null;
  externalImageUrl: string | null;
}

interface OrgWithPrimaryOrgCalendar {
  orgs: Edges<{
    primaryOrgCalendar: string;
  }>;
}

interface Props {
  hideAdvancedMenu?: boolean;
  shouldNavigate: boolean;
  viewer: {
    user: (UserWithOrgs & UserWithFlowStates & UserProfile & OrgWithPrimaryOrgCalendar) | null;
  };
}

export const WebAppNavAvatarMenu = ({ hideAdvancedMenu, viewer, shouldNavigate }: Props) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLDivElement>(null);
  const [advancedAnchorEl, setAdvancedAnchorEl] = React.useState<null | HTMLDivElement>(null);

  const fastCrawlFinished = useSelector((state: IReduxState) => state.onboarding.fastCrawlFinished);

  const { router } = useRouter();
  const isOutlook = useIsOutlook();

  //////////////
  // Handlers //
  //////////////
  const onOpenMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    track(TrackingEvents.APP_NAV.OPEN_MENU);

    setAnchorEl(event.currentTarget);
  };

  const onOpenAdvancedMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    track(TrackingEvents.APP_NAV.OPEN_ADVANCED_MENU);

    setAdvancedAnchorEl(event.currentTarget);
  };

  const onCloseMenu = () => {
    setAnchorEl(null);
  };

  const onCloseAdvancedMenu = () => {
    setAdvancedAnchorEl(null);
  };

  const onClickSettings = () => {
    track(TrackingEvents.APP_NAV.MENU_SETTINGS);
    onCloseMenu();

    if (shouldNavigate) {
      router.replace(appPaths.landing);
    }
  };

  const onClickLogout = () => {
    track(TrackingEvents.APP_NAV.MENU_LOGOUT);
    onCloseMenu();
    if (isOutlook) {
      // Close the Outlook add-in. Simplifies where to direct users after signing out.
      window.Office.context.ui.closeContainer();
    }
    if (shouldNavigate) {
      router.replace(paths.login);
    }
  };

  const { user } = viewer;

  if (!user) {
    return null;
  }

  const onboardingState = user.flowStates ? getOnboardingFlowState(user) : null;

  const org = getCurrentOrg(viewer);

  if (!org) {
    return null;
  }

  const showAdvancedActions =
    onboardingState && onboardingState.current.state === "Finished" && fastCrawlFinished;

  // const userProfile =
  //   org &&
  //   org.userPerson &&
  //   org.userPerson.__typename !== "GraphEntityError" &&
  //   org.userPerson &&
  //   org.userPerson.profile;

  // const externalImageUrl = ;

  const avatarAlt = user.givenName
    ? `${user.givenName} ${user.familyName || ""}`.trim()
    : (org && org.primaryOrgCalendar) || "";

  return (
    <div className={classes.menus}>
      {showAdvancedActions && !hideAdvancedMenu && (
        <React.Fragment>
          <div className={classes.advancedMenu} onClick={onOpenAdvancedMenu}>
            More
            <ArrowDropDown className={classes.menuExpandIcon} />
          </div>
          <Menu
            className={classes.menu}
            open={!!advancedAnchorEl}
            anchorEl={advancedAnchorEl}
            getContentAnchorEl={null}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            onClose={onCloseAdvancedMenu}
            onBackdropClick={onCloseAdvancedMenu}
            onEscapeKeyDown={onCloseAdvancedMenu}
          >
            <MenuItem
              onClick={() => {
                track(TrackingEvents.APP_NAV.MENU_FEEDBACK);
                window.open("https://support.getclockwise.com/hc/en-us/requests/new", "_blank");
                onCloseAdvancedMenu();
              }}
            >
              <Feedback className={classes.menuItemIcon} />
              <ListItemText inset primary="Contact support" />
            </MenuItem>
            <MenuItem
              onClick={() => {
                track(TrackingEvents.APP_NAV.MENU_HELP);
                window.open(baseUrl, "_blank");
                onCloseAdvancedMenu();
              }}
            >
              <Help className={classes.menuItemIcon} />
              <ListItemText inset primary="Help center" />
            </MenuItem>
            <MenuItem
              onClick={() => {
                track(TrackingEvents.APP_NAV.MENU_CLOCKWISE_ACADEMY);
                window.open(CLOCKWISE_ACADEMY, "_blank");
                onCloseAdvancedMenu();
              }}
            >
              <School className={classes.menuItemIcon} />
              <ListItemText inset primary="Clockwise Academy" />
            </MenuItem>
          </Menu>
        </React.Fragment>
      )}
      <Avatar
        className={classes.avatar}
        onClick={onOpenMenu}
        alt={avatarAlt}
        src={user.externalImageUrl || undefined}
      >
        {avatarAlt[0]}
      </Avatar>
      <Menu
        className={classes.menu}
        open={!!anchorEl}
        anchorEl={anchorEl}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        onClose={onCloseMenu}
      >
        {showAdvancedActions
          ? [
              <MenuItem key="settings" onClick={onClickSettings}>
                <Settings className={classes.menuItemIcon} />
                <ListItemText inset primary="Settings" />
              </MenuItem>,
              <Divider key="settings-divider" className={classes.menuDivider} />,
            ]
          : null}
        {showAdvancedActions
          ? [
              <MenuItem
                key="account"
                onClick={() => {
                  track(TrackingEvents.APP_NAV.MENU_ACCOUNT);
                  router?.replace(appPaths.preferences);
                  onCloseMenu();
                  onCloseAdvancedMenu();
                }}
              >
                <AccountCircle className={classes.menuItemIcon} />
                <ListItemText inset primary="Account details" />
              </MenuItem>,
              <Divider key="account-divider" className={classes.menuDivider} />,
            ]
          : null}
        <LogoutBtn
          onLogout={onClickLogout}
          isMenuItem
          menuItemIcon={<ExitToApp className={classes.menuItemIcon} />}
        />
      </Menu>
    </div>
  );
};
